import { useEffect } from 'react'
import { useThemeStore, type Theme } from './store/theme'

const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)'

function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  return window.matchMedia(DARK_MEDIA_QUERY).matches
}

function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'dark') return 'dark'
  if (theme === 'light') return 'light'
  return getSystemPrefersDark() ? 'dark' : 'light'
}

function applyTheme(resolved: 'light' | 'dark') {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (resolved === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
}

interface UseThemeResult {
  readonly theme: Theme
  readonly resolvedTheme: 'light' | 'dark'
  setTheme: (theme: Theme) => void
}

export function useTheme(): UseThemeResult {
  const theme = useThemeStore((s) => s.theme)
  const setTheme = useThemeStore((s) => s.setTheme)
  const resolvedTheme = resolveTheme(theme)

  useEffect(() => {
    applyTheme(resolveTheme(theme))

    if (theme !== 'system') return
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return

    const media = window.matchMedia(DARK_MEDIA_QUERY)
    const onChange = (event: MediaQueryListEvent) => {
      applyTheme(event.matches ? 'dark' : 'light')
    }

    media.addEventListener('change', onChange)
    return () => {
      media.removeEventListener('change', onChange)
    }
  }, [theme])

  return { theme, resolvedTheme, setTheme }
}
